/**
 * V2 Routes
 *
 * All routes use the /v2 prefix and lazy-load from @/pages-v2/.
 * Each route includes meta.uiVersion = 2 and meta.hideNavbar = true
 * so the V2Layout handles its own sidebar/navigation.
 */

import store from '@/store';
import {
	ROLE_SUPER,
	ROLE_SSP_ADMIN,
	ROLE_SSP_APPROVE,
	ROLE_SSP_VIEWER,
	ROLE_DSP,
} from '@/constants/roles';


/**
 * Lazy loader for v2 page components.
 */
function loadV2Page(pageName) {
	return () => import(
		/* webpackChunkName: "v2-[request]" */
		`@/pages-v2/${pageName}`
	);
}


export const v2Routes = [
	//
	// Dashboard
	//
	{
		name: 'v2-index',
		path: '/v2',
		component: loadV2Page('dashboard'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp', 'dsp'],
			caption: 'Dashboard',
			roles: ['all'],
		},
	},

	//
	// Advertisers
	//
	{
		name: 'v2-advertiser-list',
		path: '/v2/advertisers',
		component: loadV2Page('advertisers/list'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp', 'dsp'],
			caption: 'Advertisers',
			roles: [ROLE_SUPER, ROLE_SSP_ADMIN, ROLE_SSP_APPROVE, ROLE_DSP],
		},
		children: [
			{
				name: 'v2-advertiser-add',
				path: 'add',
				component: () => import('@/pages-v2/advertisers/edit.vue'),
				meta: {
					uiVersion: 2,
					hideNavbar: true,
					access: ['dsp'],
					caption: 'Add Advertiser',
					parentRouteName: 'v2-advertiser-list',
					roles: [ROLE_DSP],
				},
			},
			{
				name: 'v2-advertiser-edit',
				path: 'edit/:advertiserId',
				component: () => import('@/pages-v2/advertisers/edit.vue'),
				props: (route) => ({
					key: `v2-advertiser-edit-${route.params.advertiserId}`,
					advertiserId: ~~route.params.advertiserId,
				}),
				meta: {
					uiVersion: 2,
					hideNavbar: true,
					access: ['ssp'],
					caption: 'Edit Advertiser',
					parentRouteName: 'v2-advertiser-list',
					roles: [ROLE_SUPER, ROLE_SSP_ADMIN, ROLE_SSP_APPROVE],
				},
			},
		],
	},

	//
	// Requests
	//
	{
		name: 'v2-request-list',
		path: '/v2/requests',
		component: loadV2Page('requests/list'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp', 'dsp'],
			caption: 'Requests',
			roles: [ROLE_SUPER, ROLE_SSP_ADMIN, ROLE_SSP_APPROVE, ROLE_DSP],
		},
	},

	{
		name: 'v2-request-details',
		path: '/v2/request/:id',
		component: loadV2Page('requests/details'),
		props: (route) => ({
			key: `v2-request-details-${route.params.id}`,
			id: ~~route.params.id,
			resource: 'request',
		}),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp', 'dsp'],
			caption: 'Request details',
			parentRouteName: 'v2-request-list',
			roles: [ROLE_SUPER, ROLE_SSP_ADMIN, ROLE_SSP_APPROVE, ROLE_DSP],
		},
		/**
		 * redirect to campaign if present
		 * TODO: do we still have several campaigns for request?
		 */
		async beforeEnter(to, from, next) {
			const id = to.params.id;

			const req = await store.dispatch('request/getItem', {id});

			// why do we check it like this?
			// historically `audit` means there is a campaign
			// but we also have a reference on the `req` object too!
			let campaign_id = null;
			for (let audit of req.status) {
					campaign_id = req.campaign;
					break;
			}

			if (campaign_id) {
				next({
					name: 'v2-campaign-details',
					params: {
						id: campaign_id,
					},
					replace: true,
				});
			}
			else {
				next();
			}
		},
	},

	{
		name: 'v2-request-add',
		path: '/v2/request-add',
		component: loadV2Page('requests/create'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['dsp'],
			caption: 'Add request',
			parentRouteName: 'v2-request-list',
			roles: [ROLE_DSP],
		},
	},

	//
	// Campaigns
	//
	{
		name: 'v2-campaign-list',
		path: '/v2/campaigns',
		component: loadV2Page('campaigns/list'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp', 'dsp'],
			caption: 'Campaigns',
			roles: ['all'],
		},
	},

	{
		name: 'v2-campaign-details',
		path: '/v2/campaign/:id',
		component: loadV2Page('campaigns/details'),
		props: (route) => ({
			key: `v2-campaign-details-${route.params.id}`,
			id: ~~route.params.id,
			resource: 'campaign',
		}),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp', 'dsp'],
			caption: 'Campaign details',
			parentRouteName: 'v2-campaign-list',
			roles: ['all'],
		},
	},

	//
	// Network
	//
	{
		name: 'v2-network',
		path: '/v2/network',
		component: loadV2Page('network'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp'],
			caption: 'Network',
			roles: [ROLE_SSP_ADMIN],
		},
	},

	//
	// Agencies
	//
	{
		name: 'v2-agency-list',
		path: '/v2/agencies',
		component: loadV2Page('agencies/list'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp'],
			caption: 'Agencies',
			roles: [ROLE_SSP_ADMIN],
		},
	},

	{
		name: 'v2-agency-balance',
		path: '/v2/agencies/balance',
		component: loadV2Page('agencies/balance'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp'],
			caption: 'Balance Management',
			parentRouteName: 'v2-agency-list',
			roles: [ROLE_SSP_ADMIN],
		},
	},

	{
		name: 'v2-agency-create',
		path: '/v2/agency/create',
		component: loadV2Page('agencies/details'),
		props: (route) => ({
			key: 'v2-agency-create',
			side: 'dsp',
			resource: 'agency',
			isSspDspEditPage: true,
		}),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp'],
			caption: 'New Agency',
			parentRouteName: 'v2-agency-list',
			roles: [ROLE_SSP_ADMIN],
		},
	},

	{
		name: 'v2-agency-details',
		path: '/v2/agency/:id',
		component: loadV2Page('agencies/details'),
		props: (route) => ({
			key: 'v2-agency-details',
			id: ~~route.params.id,
			side: 'dsp',
			resource: 'agency',
			isSspDspEditPage: true,
		}),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp'],
			caption: 'Agency details',
			parentRouteName: 'v2-agency-list',
			roles: [ROLE_SSP_ADMIN],
		},
	},

	//
	// White-label Settings
	//
	{
		name: 'v2-whitelabel-settings',
		path: '/v2/whitelabel-settings',
		component: loadV2Page('whitelabel-settings'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp'],
			caption: 'White-label Settings',
			roles: [ROLE_SSP_ADMIN],
		},
	},

	//
	// Billing
	//
	{
		name: 'v2-billing',
		path: '/v2/billing',
		component: loadV2Page('billing/index'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp', 'dsp'],
			caption: 'Billing',
			roles: [ROLE_SUPER, ROLE_SSP_ADMIN, ROLE_SSP_VIEWER, ROLE_DSP],
		},
	},

	{
		name: 'v2-balance-add',
		path: '/v2/balance-add',
		component: loadV2Page('billing/balance-add'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['dsp'],
			caption: 'Add balance',
			parentRouteName: 'v2-billing',
			roles: [ROLE_DSP],
		},
	},

	{
		name: 'v2-balance-add-success',
		path: '/v2/billing/success',
		component: loadV2Page('billing/success'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['dsp'],
			caption: 'Success',
			parentRouteName: 'v2-billing',
			roles: [ROLE_DSP],
		},
	},

	{
		name: 'v2-balance-add-fail',
		path: '/v2/billing/fail',
		component: loadV2Page('billing/fail'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['dsp'],
			caption: 'Fail',
			parentRouteName: 'v2-billing',
			roles: [ROLE_DSP],
		},
	},

	//
	// Profile
	//
	{
		name: 'v2-profile',
		path: '/v2/me',
		component: loadV2Page('user/details'),
		props: (route) => ({
			key: 'v2-profile',
			id: store.state.profile.id,
			resource: 'user',
			side: store.state.app.side,
			viewType: 'profile',
		}),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['dsp', 'ssp'],
			caption: 'Profile',
			roles: ['all'],
		},
	},

	{
		name: 'v2-agency-profile',
		path: '/v2/my-agency',
		component: loadV2Page('agencies/details'),
		props: (route) => ({
			id: store.state.agency.id,
			side: store.state.app.side,
			resource: 'agency',
		}),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['dsp', 'ssp'],
			caption: 'Agency profile',
			roles: ['all'],
		},
	},

	//
	// User management (SSP admin)
	//
	{
		name: 'v2-user-details',
		path: '/v2/agency/:agencyId/user/:id',
		component: loadV2Page('user/details'),
		props: (route) => ({
			key: `v2-user-details-${route.params.id}`,
			id: ~~route.params.id,
			agencyId: ~~route.params.agencyId,
			resource: 'user',
			side: 'dsp',
			viewType: 'child-agency-user',
		}),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp'],
			caption: 'User Details',
			parentRouteName: 'v2-profile',
			roles: [ROLE_SSP_ADMIN],
		},
	},

	{
		name: 'v2-user-ssp-viewer-details',
		path: '/v2/agency/:agencyId/user/:id/ssp-viewer',
		component: loadV2Page('user/ssp-viewer-details'),
		props: (route) => ({
			key: `v2-user-ssp-viewer-details-${route.params.id}`,
			id: ~~route.params.id,
			agencyId: ~~route.params.agencyId,
		}),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['ssp'],
			caption: 'SSP Viewer Details',
			parentRouteName: 'v2-profile',
			roles: [ROLE_SSP_ADMIN],
		},
		children: [
			{
				name: 'v2-user-ssp-viewer-network-devices',
				path: 'network-devices/:networkId',
				component: loadV2Page('user/ssp-viewer-devices'),
				props: (route) => ({
					key: `v2-user-ssp-viewer-network-devices-${route.params.id}-${route.params.networkId}`,
					id: ~~route.params.id,
					agencyId: ~~route.params.agencyId,
					networkId: ~~route.params.networkId,
				}),
				meta: {
					uiVersion: 2,
					hideNavbar: true,
					access: ['ssp'],
					caption: 'SSP Viewer Network Devices',
					parentRouteName: 'v2-profile',
					roles: [ROLE_SSP_ADMIN],
				},
			},
		],
	},

	//
	// Auth pages (V2)
	//
	{
		name: 'v2-login',
		path: '/v2/login',
		component: loadV2Page('auth/login'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['guest', 'all'],
			roles: ['all'],
		},
	},
	{
		name: 'v2-registration',
		path: '/v2/registration',
		component: loadV2Page('auth/registration'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['guest', 'all'],
			roles: ['all'],
		},
	},
	{
		name: 'v2-choose-agency',
		path: '/v2/choose-agency',
		component: loadV2Page('auth/choose-agency'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['guest', 'all'],
			roles: ['all'],
		},
	},
	{
		name: 'v2-reset-password',
		path: '/v2/reset-password',
		component: loadV2Page('auth/reset-password'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['guest', 'all'],
			roles: ['all'],
		},
	},
	{
		name: 'v2-change-password',
		path: '/v2/change-password/:urlToken',
		component: loadV2Page('auth/change-password'),
		props: route => ({ urlToken: route.params.urlToken }),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['guest', 'all'],
			roles: ['all'],
		},
	},
	{
		name: 'v2-email-verified',
		path: '/v2/email-verified',
		component: loadV2Page('auth/email-verified'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['guest', 'all'],
			roles: ['all'],
		},
	},

	//
	// 404 catch-all for /v2/*
	//
	{
		name: 'v2-page404',
		path: '/v2/*',
		component: loadV2Page('error/404'),
		meta: {
			uiVersion: 2,
			hideNavbar: true,
			access: ['guest', 'all'],
			roles: ['all'],
		},
	},
];

export default v2Routes;
